import type { CSSObject } from "type/tss";
import { Theme } from "@mui/material/styles";
import { designTokens } from "theme";

// Column layout, shared between the header row and each body row so the div-based
// (non-table) layout stays aligned. react-beautiful-dnd needs a stable DOM, so we
// render flex rows rather than a <table>.
// Figma widths (table = 1087): Include 96 (fixed) · Property Type 309.5 · Display Name 372 ·
// Order 309.5 → the three non-fixed columns use flex in a 5 : 6 : 5 ratio.
const COL_INCLUDE = 96;
const FLEX_TYPE = 5;
const FLEX_NAME = 6;
const FLEX_ORDER = 5;

export const styles = (theme: Theme) => {
	return ({
		// --- Table (div-based) ---
		table: {
			border: `1px solid ${theme.palette.custom.borderCard}`,
			borderRadius: 8,
			overflow: "hidden",
		},
		headerCells: {
			display: "flex",
			alignItems: "center",
			padding: 16,
			gap: 12,
			backgroundColor: theme.palette.custom.tableHeaderSurface,
			borderBottom: `1px solid ${theme.palette.custom.divider}`,
			// Figma header type = Body2: SF Pro Regular 400, 16/24, text/secondary (#5f6368) — not bold.
			color: theme.palette.text.secondary,
			fontWeight: 400,
			fontSize: 16,
			lineHeight: "24px",
		},
		row: {
			display: "flex",
			alignItems: "center",
			padding: 16,
			gap: 12,
			backgroundColor: theme.palette.background.paper,
			borderBottom: `1px solid ${theme.palette.custom.divider}`,
		},
		rowLast: {
			borderBottom: "none",
		},
		// Unchecked/disabled row: the whole row tints to Surface/field-disabled (#f8f8f8) and the
		// Display Name field blends in (see nameInput disabled overrides) rather than showing a
		// heavy boxed field — matches Figma.
		rowDisabled: {
			backgroundColor: theme.palette.custom.tableHeaderSurface,
		},
		colInclude: {
			width: COL_INCLUDE,
			flexShrink: 0,
		},
		colType: {
			flex: FLEX_TYPE,
			minWidth: 0,
		},
		colName: {
			flex: FLEX_NAME,
			minWidth: 0,
		},
		colOrder: {
			flex: FLEX_ORDER,
			minWidth: 0,
			display: "flex",
			justifyContent: "flex-end",
		},
		typeName: {
			fontWeight: 400,
		},
		nameInput: {
			width: "100%",
			// Disabled (row unchecked): blend into the tinted row — transparent fill, soft
			// Border/field-disabled (#ebebeb), Text/field-entered-disabled (#939393) placeholder.
			"& .MuiOutlinedInput-root.Mui-disabled": {
				backgroundColor: "transparent",
			},
			"& .MuiOutlinedInput-root.Mui-disabled .MuiOutlinedInput-notchedOutline": {
				borderColor: theme.palette.custom.divider,
			},
			"& .MuiOutlinedInput-input.Mui-disabled": {
				color: "#939393",
			},
		},
		// Filled orange rounded-square checkbox (Figma "Include" control). MUI's default is an
		// outlined checkmark, so we supply custom icon/checkedIcon spans.
		includeCheckbox: {
			padding: 0,
			"&:hover": {
				backgroundColor: "transparent",
			},
		},
		checkboxIcon: {
			display: "inline-flex",
			alignItems: "center",
			justifyContent: "center",
			width: 20,
			height: 20,
			borderRadius: 4,
			// Figma Surface/checkbox-off-default #b9b9b9.
			border: "1.5px solid #b9b9b9",
			boxSizing: "border-box",
			color: designTokens.colors.white,
		},
		checkboxIconChecked: {
			backgroundColor: designTokens.colors.brandOrange,
			borderColor: designTokens.colors.brandOrange,
		},
		dragHandle: {
			display: "inline-flex",
			cursor: "grab",
			color: theme.palette.text.secondary,
			"&:active": {
				cursor: "grabbing",
			},
		},
		dragHandleDisabled: {
			cursor: "default",
			opacity: 0.35,
		},

		// --- Unavailable accordion (separate white card matching the table) ---
		accordion: {
			marginTop: 16,
			border: `1px solid ${theme.palette.custom.borderCard}`,
			borderRadius: 8,
			backgroundColor: theme.palette.background.paper,
			boxShadow: "none",
			"&:before": {
				display: "none",
			},
			// MUI adds a top margin to the expanded accordion — cancel it so it stays a fixed card.
			"&.Mui-expanded": {
				margin: "16px 0 0",
			},
		},
		// Chevron sits on the LEFT of the label (Figma), so reverse the summary row and pull the
		// label back against the icon.
		accordionSummary: {
			flexDirection: "row-reverse",
			fontWeight: 700,
		},
		accordionSummaryContent: {
			marginLeft: 8,
		},
		accordionExpandIcon: {
			color: theme.palette.custom.tertiaryAction,
			padding: 0,
			marginRight: 0,
		},
		accordionBody: {
			display: "block",
		},
		// Figma: Body1 SF Pro Regular 18/24, text/primary (#1c1b1f).
		accordionDescription: {
			fontSize: 18,
			lineHeight: "24px",
			color: theme.palette.custom.textPrimary,
		},
		bulletList: {
			// Figma: description → list gap is spacing/md (16px); disc bullets indented 27px;
			// items use text/primary (#1c1b1f) 18/24, same as the description.
			margin: "16px 0 0",
			paddingLeft: 27,
			color: theme.palette.custom.textPrimary,
			fontSize: 18,
			lineHeight: "24px",
		},

		// --- Restore Defaults link (top-right, above the table) ---
		restoreRow: {
			display: "flex",
			justifyContent: "flex-end",
			marginBottom: 16,
		},
		// Teal tertiary-action text link. Figma type = Button/Medium: SF Pro Semibold 590, 16/20.
		// Reset the MUI button-link's native button appearance.
		restoreLink: {
			color: theme.palette.custom.tertiaryAction,
			fontSize: 16,
			fontWeight: 590,
			lineHeight: "20px",
			border: "none",
			background: "none",
			padding: 0,
			cursor: "pointer",
			"&:hover": {
				textDecoration: "none",
			},
		},
	} satisfies Record<string, CSSObject>);
};
